/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Link, Tooltip } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import { ROUTES } from '../../../../constants/routes';
import { Tab } from '../../../../components/Tab';
import { CustomPagination } from '../../../../components/CustomPagination';
import { CircularProgressCenter } from '../../../../components/CircularProgressCenter';
import EnhancedTable from '../../../../components/EnhancedTable';
import { DetailedTableBody } from '../../components/DetailedTableBody';
import { HEAD_CELL_DETAILED } from '../../constants/HeadCellDetailed';
import { Application } from '../../../my-applications/components/Application';
import { SortApplication } from '../../../my-applications/components/SortApplication';
import {
  getSearchCustomer,
  getApplications,
  GetTransactions,
  GetBalance,
  GetBonuses
} from '../../../../store/support/actions';
import { APPLICATION_SORT_OPTIONS, DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import CustomerPaper from '../../components/CustomerPaper/CustomerPaper';
import { useWebSocket } from '../../../../hooks/useWebSocket';
import { useSearchCustomerBy } from '../../../../hooks/useSearchCustomerBy';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';

const DETAILING = 'DETAILING';
const APPLICATIONS = 'APPLICATIONS';

export const UserCard = () => {
  const { data } = useWebSocket();
  const params = useParams();

  const dispatch = useDispatch();
  const { getParams } = useSearchCustomerBy();

  const { customer, applications, applicationsFetching, transactions } = useSelector((state) => state.support);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const isDisabledFilterCustomerPermission = !visibleBySectionPermission(permissions, PERMISSIONS.SUPPORT.ListSupportBonusRequests.value);
  const isDisabledListCustomerAccountPermission = !visibleBySectionPermission(permissions, PERMISSIONS.SUPPORT.RetrieveSupportBonusRequests.value);

  const [typeTable, setTypeTable] = useState(
    params.tab === APPLICATIONS ? APPLICATIONS : DETAILING
  );
  const [filterDetailing, setFilterDetailing] = useState({
    orderBy: 'created_at',
    orderType: 'desc',
    page: 0,
    size: transactions?.size || 10,
  });

  const [filterApplication, setFilterApplication] = useState({
    order_by: APPLICATION_SORT_OPTIONS[0],
    order_type: 'desc',
    page: 1,
    size: 8,
  });

  const transactionsPayload = {
    // BE sucks with field name here for campaign
    order_by: filterDetailing.orderBy === 'campaign' ? 'company_name' : filterDetailing.orderBy,
    order_type: filterDetailing.orderType,
    page: filterDetailing.page + 1,
    size: filterDetailing.size,
  };

  useEffect(() => {
    if (!Object.values(customer).length) {
      dispatch(getSearchCustomer(getParams())).then((res) => {
        dispatch(GetBalance(res.data.iin));
        dispatch(GetTransactions(res.data.iin, transactionsPayload));
        dispatch(GetBonuses(res.data.iin))
      });
    } else {
      dispatch(GetBalance(customer.iin));
      dispatch(GetBonuses(customer.iin));
    }
  }, // eslint-disable-next-line
    [

  ]);

  useEffect(() => {
    customer.iin && dispatch(GetTransactions(customer.iin, transactionsPayload));
  }, // eslint-disable-next-line
    [
    filterDetailing.orderBy,
    filterDetailing.orderType,
    filterDetailing.page,
    filterDetailing.size,
  ]);

  useEffect(() => {
    dispatch(
      getApplications({
        ...filterApplication,
        order_by: filterApplication.order_by?.value,
        customer_iin: customer?.iin,
        subscriber_status: customer?.status,
      })
    );  // eslint-disable-next-line
  }, [filterApplication, data]);

  const setTypeTableUseCallback = useCallback((e) => setTypeTable(e), []);

  return (
    <Box>
      <Breadcrumbs>
        <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.SUPPORT}>
          Техподдержка
        </Link>
        <Typography>{customer?.name}</Typography>
      </Breadcrumbs>

      <CustomerPaper />

      <Box sx={{ display: 'flex' }}>
        <Tab
          label="Детализация"
          setTypeTable={setTypeTableUseCallback}
          typeCurrent={typeTable}
          typeDefault={DETAILING}
        />
        <Tab
          label="Заявки"
          setTypeTable={setTypeTableUseCallback}
          typeCurrent={typeTable}
          typeDefault={APPLICATIONS}
          disabled={isDisabledListCustomerAccountPermission}
          tooltip={isDisabledListCustomerAccountPermission ? DISABLED_CONTROL_TOOLTIP : null}
          data-testid="ApplicationsTab"
        />
      </Box>

      {typeTable === DETAILING && (
        <EnhancedTable
          rows={transactions?.items || []}
          headCells={HEAD_CELL_DETAILED}
          filter={filterDetailing}
          total={transactions?.total || 0}
          setFilter={setFilterDetailing}>
          <DetailedTableBody data={transactions?.items} />
        </EnhancedTable>
      )}

      {typeTable === APPLICATIONS && (
        <>
          <SortApplication
            filter={filterApplication}
            setFilter={setFilterApplication}
            total={applications?.total}
            disabled={isDisabledFilterCustomerPermission}
            tooltip={isDisabledFilterCustomerPermission ? DISABLED_CONTROL_TOOLTIP : null}
          />

          {applicationsFetching ? (
            <CircularProgressCenter marginBottom="150px" data-testid="CircularProgressCenter" />
          ) : (
            applications?.items?.map((application) => (
              <Application
                key={application.id}
                numberApplication={application.request_num}
                nameCreator={application.manager.fullname}
                dateCreate={application.created_at}
                SLA={application.sla}
                // bonusesNumber={application.subscriber.account_number}
                status={application.request_status}
                id={application.id}
                userPermissions={permissions}
              />
            ))
          )}
          <Box display="flex" justifyContent="flex-end" marginTop="24px" data-testid="CustomPaginationForApplications" >
            <CustomPagination
              onChange={(event, newPage) =>
                setFilterApplication({ ...filterApplication, page: newPage })
              }
              page={filterApplication.page}
              count={Math.ceil(applications?.total / applications?.size) || 0}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
